import React from 'react';
import { Card, CardContent } from './Card';
import { Skeleton } from './Skeleton';
import { formatCurrency, formatCompactCurrency } from '@/utils/format';
import { TrendingUp, TrendingDown, LucideIcon } from 'lucide-react';
import { cn } from '@/utils/cn';

export interface KPICardProps {
  title: string;
  value: string | number;
  icon?: string | LucideIcon;
  trend?: {
    value: number;
    isPositive: boolean;
  };
  variant?: 'default' | 'neutral' | 'success' | 'warning' | 'danger' | 'info' | 'primary';
  color?: 'blue' | 'green' | 'red' | 'amber' | 'slate'; // Compatibility prop
  format?: 'currency' | 'compact-currency' | 'percentage' | 'number' | 'none';
  suffix?: string;
  className?: string;
  isLoading?: boolean;
}

export const KPICard = ({
  title,
  value,
  icon: Icon,
  trend,
  variant: propVariant,
  color,
  format = 'none',
  suffix = '',
  className = '',
  isLoading
}: KPICardProps) => {
  // Map color to variant for backward compatibility
  const variant = propVariant || (
    color === 'blue' ? 'primary' :
    color === 'green' ? 'success' :
    color === 'red' ? 'danger' :
    color === 'amber' ? 'warning' :
    'default'
  );

  const variantStyles = {
    default: {
      accent: 'bg-slate-500',
      border: 'group-hover:border-slate-300',
      text: 'text-slate-600',
      bg: 'bg-slate-100',
      glow: 'group-hover:shadow-slate-200/40'
    },
    neutral: {
      accent: 'bg-slate-500',
      border: 'group-hover:border-slate-300',
      text: 'text-slate-600',
      bg: 'bg-slate-100',
      glow: 'group-hover:shadow-slate-200/40'
    },
    success: {
      accent: 'bg-emerald-500',
      border: 'group-hover:border-emerald-300',
      text: 'text-emerald-600',
      bg: 'bg-emerald-50',
      glow: 'group-hover:shadow-emerald-200/40'
    },
    warning: {
      accent: 'bg-amber-500',
      border: 'group-hover:border-amber-300',
      text: 'text-amber-600',
      bg: 'bg-amber-50',
      glow: 'group-hover:shadow-amber-200/40'
    },
    danger: {
      accent: 'bg-rose-500',
      border: 'group-hover:border-rose-300',
      text: 'text-rose-600',
      bg: 'bg-rose-50',
      glow: 'group-hover:shadow-rose-200/40'
    },
    info: {
      accent: 'bg-sky-500',
      border: 'group-hover:border-sky-300',
      text: 'text-sky-600',
      bg: 'bg-sky-50',
      glow: 'group-hover:shadow-sky-200/40'
    },
    primary: {
      accent: 'bg-blue-600',
      border: 'group-hover:border-blue-300',
      text: 'text-blue-600',
      bg: 'bg-blue-50',
      glow: 'group-hover:shadow-blue-200/40'
    }
  };

  const style = variantStyles[variant as keyof typeof variantStyles] || variantStyles.default;

  const getFormattedValue = () => {
    switch (format) {
      case 'currency':
        return formatCurrency(value, suffix || 'VNĐ');
      case 'compact-currency':
        return formatCompactCurrency(value, suffix || 'VNĐ');
      case 'percentage':
        return `${value}%`;
      case 'number':
        return typeof value === 'number' ? value.toLocaleString('vi-VN') : value;
      default:
        return suffix ? `${value} ${suffix}` : value;
    }
  };

  if (isLoading) {
    return (
      <Card className={cn("overflow-hidden border border-slate-100 bg-white shadow-sm rounded-xl", className)}>
        <div className="p-3 sm:p-4 flex flex-col gap-3">
          <Skeleton className="h-8 w-8 rounded-lg" />
          <Skeleton className="h-3 w-3/4 rounded-md mt-1" />
          <Skeleton className="h-6 w-1/2 rounded-md mt-2" />
        </div>
      </Card>
    );
  }

  const displayValue = getFormattedValue();

  return (
    <Card 
      className={cn(
        "group relative overflow-hidden bg-white border border-slate-100 transition-all duration-300 rounded-xl",
        "shadow-sm hover:-translate-y-0.5",
        style.border,
        style.glow,
        className
      )}
    >
      {/* Animated Top Border */}
      <div className={cn(
        "absolute top-0 left-0 w-full h-[3px] transition-transform duration-300 origin-left scale-x-0 group-hover:scale-x-100",
        style.accent
      )} />

      <CardContent className="p-3 sm:p-4 flex flex-col h-full justify-between relative z-10">
        <div>
          {Icon && (
            <div className={cn(
              "w-8 h-8 sm:w-10 sm:h-10 rounded-lg flex items-center justify-center mb-2 sm:mb-3 transition-transform duration-300 group-hover:scale-110",
              style.bg,
              style.text
            )}>
              {typeof Icon === 'string' ? (
                <i className={cn(Icon, "text-base sm:text-lg")} />
              ) : (
                <Icon className="w-4 h-4 sm:w-5 sm:h-5" />
              )}
            </div>
          )}
          <h3 className="text-xs sm:text-sm font-medium text-slate-500 line-clamp-2 leading-snug group-hover:text-slate-700 transition-colors">
            {title}
          </h3>
        </div>

        <div className="mt-2 sm:mt-3">
          <p className="text-xl sm:text-2xl font-bold text-slate-900 tracking-tight">
            {displayValue}
          </p>
          
          {trend && (
            <div className="flex items-center gap-1 mt-1.5 sm:mt-2">
              <span className={cn(
                "inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[10px] sm:text-xs font-medium tracking-wide",
                trend.isPositive ? "bg-emerald-50 text-emerald-600" : "bg-rose-50 text-rose-600"
              )}>
                {trend.isPositive ? <TrendingUp className="w-3 h-3" /> : <TrendingDown className="w-3 h-3" />}
                {trend.value}%
              </span>
              <span className="text-[10px] sm:text-xs text-slate-400 truncate hidden sm:inline-block">
                so với tháng trước
              </span>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
};
